import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { API } from "@/lib/api";
import { LogoMark } from "./Logo";

const SUGGEST = ["How does it work?", "How much are the plans?", "Can you swap the person in my photo?"];
const sessionId = () => {
  let id = localStorage.getItem("fs_chat_id");
  if (!id) {
    id = `s_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
    localStorage.setItem("fs_chat_id", id);
  }
  return id;
};

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([{ role: "assistant", text: "Hi! I'm Shift, Frame Shift's AI assistant. Ask me anything about swaps, plans or credits." }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const end = useRef(null);

  useEffect(() => {
    end.current?.scrollIntoView({ block: "end" });
  }, [msgs, open]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    setInput("");
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", text: q }, { role: "assistant", text: "" }]);
    try {
      const res = await fetch(`${API}/assistant/chat`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ session_id: sessionId(), message: q }) });
      if (!res.ok || !res.body) throw new Error("bad response");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const lines = buf.split("\n\n");
        buf = lines.pop() || "";
        for (const l of lines) {
          const d = l.replace(/^data: /, "");
          if (!d || d === "[DONE]") continue;
          const t = JSON.parse(d)?.t ?? "";
          setMsgs((m) => [...m.slice(0, -1), { role: "assistant", text: m[m.length - 1].text + t }]);
        }
      }
    } catch {
      setMsgs((m) => [...m.slice(0, -1), { role: "assistant", text: "Sorry, I couldn't connect. Email bennyobsaint3@gmail.com or call 941-292-9244." }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-[70]">
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ duration: 0.25 }}
            className="absolute bottom-20 right-0 w-[calc(100vw-2rem)] sm:w-[380px] h-[520px] max-h-[75vh] rounded-[24px] bg-[#0b0b0d]/95 backdrop-blur-xl border border-white/10 shadow-[0_30px_100px_-20px_rgba(190,197,208,0.6)] flex flex-col overflow-hidden"
            data-lenis-prevent
          >
            <div className="flex items-center gap-3 px-5 py-4 border-b border-white/10">
              <LogoMark className="h-8 w-8" />
              <div className="flex-1">
                <div className="font-display font-semibold text-sm">Shift · AI assistant</div>
                <div className="text-xs text-emerald-300 flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> Online 24/7</div>
              </div>
              <button data-testid="chat-close" onClick={() => setOpen(false)} className="h-8 w-8 grid place-items-center rounded-full hover:bg-white/10"><X className="h-4 w-4" /></button>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="chat-messages">
              {msgs.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-[#3b4049] text-white rounded-br-md" : "bg-white/[0.06] text-slate-100 rounded-bl-md"}`}>
                    {m.text || <Loader2 className="h-4 w-4 animate-spin text-[#d5d9df]" />}
                  </div>
                </div>
              ))}
              {msgs.length === 1 && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {SUGGEST.map((s, i) => (
                    <button key={s} data-testid={`chat-suggest-${i}`} onClick={() => send(s)} className="text-xs px-3 py-2 rounded-full border border-[#d5d9df]/30 text-[#e6e8ec] hover:bg-[#3b4049]/15">{s}</button>
                  ))}
                </div>
              )}
              <div ref={end} />
            </div>
            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 border-t border-white/10 flex gap-2">
              <input data-testid="chat-input" value={input} onChange={(e) => setInput(e.target.value)} maxLength={1000} placeholder="Ask anything…"
                className="flex-1 rounded-full bg-white/[0.04] border border-white/10 px-4 py-2.5 text-sm focus:outline-none focus:border-[#d5d9df]/60" />
              <button data-testid="chat-send" disabled={busy || !input.trim()} className="h-10 w-10 rounded-full bg-[#3b4049] grid place-items-center disabled:opacity-40"><Send className="h-4 w-4" /></button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
      <button data-testid="chat-toggle" onClick={() => setOpen((o) => !o)} aria-label="Open AI assistant"
        className="h-14 w-14 rounded-full bg-gradient-to-br from-[#8b929c] to-[#2a2e35] grid place-items-center shadow-[0_10px_40px_-5px_rgba(190,197,208,0.9)] ring-1 ring-white/20 transition-transform hover:scale-105">
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </button>
    </div>
  );
};
